import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { Consumer } from '@rails/actioncable';
import { createConsumer } from '@rails/actioncable';
import * as Sentry from '@sentry/react';
import { client } from '@treadinc/horizon-api-spec';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { t } from 'i18next';
import { get, isString, pickBy } from 'lodash-es';
import { runInAction } from 'mobx';
import { v4 as UUID } from 'uuid';

import { API_BASE_URL, SESSION_JWT_HEADER } from '~constants/consts';
import { STATUS_MESSAGES } from '~constants/errorMessagesConsts';
import { ERROR_TYPES } from '~constants/errorTypes';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import {
  endUnauthenticatedSession,
  getActiveToken,
  getImpersonationToken,
  getPrimaryToken,
  getSessionTokenScope,
  isImpersonating,
  setImpersonationToken,
  setPrimaryToken,
} from '~services/sessionTokens';
import { rootStore } from '~store';
import { alert } from '~types/AlertTypes';
import { isServerError } from '~utils/http';
import {
  isActionableApiError,
  normalizeHttpClientErrorPath,
} from '~utils/sentry/sentry-reporting-utils';

const getCurrentPageFromPath = (path: string): string => {
  const pathSegments = path.split('/').filter(Boolean);
  return pathSegments.length > 0 ? pathSegments[0] : 'home';
};

const emitError = (
  message: string | React.ReactNode,
  includeTreadAlertedText = false,
  userAction?: string,
) => {
  runInAction(() => {
    const errorMessage = includeTreadAlertedText ? (
      <Box>
        <Typography variant="body1">{message}</Typography>
        <Typography variant="body2">{t('error_messages.tread_alerted')}</Typography>
      </Box>
    ) : (
      message
    );

    const messageText = typeof message === 'string' ? message : 'Complex error message'; // Handle React node messages

    const currentPath = window.location.pathname;
    const currentPage = getCurrentPageFromPath(currentPath);

    AnalyticsController.track('error_toast_displayed', {
      page: currentPage,
      path: currentPath,
      errorMessage: messageText,
      userAction: userAction || '',
    });

    rootStore.toasterStore.push(alert(errorMessage, 'error'), true);
  });
};
const REAL_TIME_URL = import.meta.env.TREAD__RTU_URL || 'ws://localhost:8080';
const API_ERROR_FINGERPRINT_PREFIX = 'api-error';

// Errors the response interceptor has already reported to Sentry, so
// `handleRequestError` can tell that from an identically shaped error that never
// went through the interceptor. Weak, so entries go away with the errors.
const errorsCapturedInSentry = new WeakSet<object>();

// configure our generated api spec client, see https://heyapi.vercel.app/openapi-ts/clients/axios.html
client.setConfig({ baseURL: API_BASE_URL, throwOnError: true });

// Strip host + query from an API URL so only the pathname is logged. Query
// strings can carry tokens/emails, so they are dropped (PII-safe). Handles both
// absolute and relative URLs.
export const cleanApiPath = (url?: string) => {
  if (!url) {
    return '';
  }

  try {
    return new URL(url, API_BASE_URL).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const getActionableApiError = (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.response) {
    return undefined;
  }

  const { response } = error;

  if (!isActionableApiError(response.status, error.config?.method)) {
    return undefined;
  }

  return { error, response };
};

const getFailedRequestMethod = (error: AxiosError) =>
  error.config?.method?.toUpperCase() ?? 'UNKNOWN';

// A 409 AASM::InvalidTransition renders the rejected `model` + `field` plus the
// `state` it was in and the `event` it refused (_state_error.json.jbuilder).
// Emit those (omitted, not blanked, so an "is set" filter stays honest); skip
// `message` (can echo user input) and `error.code` (restates `status`).
const getApiErrorIdentifiers = (responseData: unknown) => {
  const detail = get(responseData, 'error.errors[0]');

  return pickBy(
    {
      model: get(detail, 'model'),
      field: get(detail, 'field'),
      state: get(detail, 'state'),
      event: get(detail, 'event'),
    },
    isString,
  );
};

// Emit a structured event for EVERY actionable failed API response — including
// silent failures that never surface a toast — so session replays can be
// correlated to the exact failing route, status and record.
// PII-safe: method, path (query stripped), status, and bare identifiers from
// the error body; never messages or bodies.
export const emitApiError = (unknownError: unknown) => {
  const actionableApiError = getActionableApiError(unknownError);

  if (!actionableApiError) {
    return;
  }

  const { error, response } = actionableApiError;
  const identifiers = getApiErrorIdentifiers(response.data);

  // Both paths are emitted on purpose: `path` keeps the real record ids so a
  // single failure can be pinpointed, while `route` collapses them to `:id`.
  // `route` is what Sentry fingerprints http-client errors on, so the two sides
  // join on an exact string, and it keeps the property's cardinality bounded
  // enough to trend by endpoint.
  const requestUrl = error.config?.url;

  AnalyticsController.track('api_error', {
    method: getFailedRequestMethod(error),
    path: cleanApiPath(requestUrl),
    route: requestUrl ? (normalizeHttpClientErrorPath(requestUrl) ?? '') : '',
    status: response.status,
    ...identifiers,
  });
};

// Absolute and query-stripped so the Sentry ignore rules can match on host and on
// an end-anchored path. Never throws: this runs inside the axios rejection
// interceptor, where a throw would replace the rejection the caller is awaiting.
const buildFailedRequestUrl = (url?: string) => {
  if (!url) {
    return undefined;
  }

  try {
    return `${new URL(url, API_BASE_URL).origin}${cleanApiPath(url)}`;
  } catch {
    return undefined;
  }
};

// Shaped like an httpClientIntegration event on purpose, so every rule in
// `~utils/sentry/ignore-rules` applies to it unchanged. Scope event processors run
// after all others, which is what keeps `httpContextIntegration` from replacing
// this `request` with the page URL.
export const captureApiErrorInSentry = (unknownError: unknown) => {
  const actionableApiError = getActionableApiError(unknownError);

  if (!actionableApiError) {
    return;
  }

  const { error, response } = actionableApiError;
  const status = response.status;
  const method = getFailedRequestMethod(error);
  const requestUrl = error.config?.url;
  const route = requestUrl ? normalizeHttpClientErrorPath(requestUrl) : undefined;

  const failedRequestUrl = buildFailedRequestUrl(requestUrl);

  Sentry.withScope((scope) => {
    scope.addEventProcessor((event) => {
      event.request = {
        ...event.request,
        method,
        ...(failedRequestUrl ? { url: failedRequestUrl } : {}),
      };
      event.contexts = {
        ...event.contexts,
        response: { ...event.contexts?.response, status_code: status },
      };

      return event;
    });

    scope.setExtra('error_status', status);
    Object.entries(getApiErrorIdentifiers(response.data)).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    // `request.url` holds the failed call, not the page, so the screen the user was
    // on has to be recorded separately.
    scope.setExtra('page_path', window.location.pathname);

    if (route) {
      scope.setFingerprint([API_ERROR_FINGERPRINT_PREFIX, method, route, String(status)]);
    }

    Sentry.captureException(error);
  });

  errorsCapturedInSentry.add(unknownError as object);
};

// Stamps every outgoing request with the session it is acting as - the
// impersonation token while one is running, the user's own otherwise - plus the
// tracing ids the API correlates a request by.
export const authenticateRequest = (config: InternalAxiosRequestConfig) => {
  const token = getActiveToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers.Accept = 'application/json';

  const requestId = UUID();
  config.headers['X-Request-Id'] = requestId;
  config.headers['X-Amzn-Trace-Id'] = `RequestId=${requestId}`;

  return config;
};

class Connection {
  private realTimeConnectionInstance: Consumer;

  public get realTimeConnection(): Consumer {
    return this.realTimeConnectionInstance;
  }

  constructor() {
    this.realTimeConnectionInstance = createConsumer(REAL_TIME_URL);

    const token = getActiveToken() || '';
    if (token?.length) {
      this.realTimeConnectionInstance.addSubProtocol(token);
    }

    this.initializeInterceptors();
  }

  private initializeInterceptors(): void {
    // No rejection handler: axios propagates a request-setup failure unchanged
    // when none is registered, which is all the previous one did.
    client.instance.interceptors.request.use(authenticateRequest);

    // Ensure that the handler has access to object methods and context
    const boundHandler = this.handleSuccessMiddleware.bind(this);
    client.instance.interceptors.response.use(boundHandler, (error) => {
      // Both fire for every non-2xx response regardless of how the caller handles
      // it, so silent failures (e.g. teardown requests that never toast) are still
      // reported. Re-reject so the existing handleRequestError flow is unchanged.
      emitApiError(error);
      captureApiErrorInSentry(error);
      return Promise.reject(error);
    });
  }

  private reconnectRealTimeConnection(token: string): void {
    this.realTimeConnectionInstance.disconnect();
    this.realTimeConnectionInstance.subprotocols = [];
    this.realTimeConnectionInstance.addSubProtocol(token);
    this.realTimeConnectionInstance.connect();
  }

  // public so the token-bucketing rules below can be driven directly by tests;
  // in the app it is only ever reached as the response interceptor.
  public handleSuccessMiddleware(response: AxiosResponse): AxiosResponse {
    const refreshedToken = response?.headers?.[SESSION_JWT_HEADER] ?? '';

    if (!refreshedToken) {
      return response;
    }

    // a refreshed token is written back only to the session it belongs to, read
    // off the token's own audience claim. any endpoint may return one, and
    // storing an impersonation-scoped token as the admin's own would leave them
    // acting as the impersonated user with no way to stop.
    const scope = getSessionTokenScope(refreshedToken);

    if (scope === 'impersonation') {
      // opening an impersonated session is a transition owned by
      // `startImpersonation`, which also records the impersonator and reloads;
      // the interceptor only ever refreshes one that is already running.
      if (isImpersonating() && refreshedToken !== getImpersonationToken()) {
        setImpersonationToken(refreshedToken);
        this.reconnectRealTimeConnection(refreshedToken);
      }

      return response;
    }

    // an audience the client does not recognize is left alone rather than
    // guessed at: the stored tokens stay valid until they expire on their own.
    if (scope === 'primary' && refreshedToken !== getPrimaryToken()) {
      setPrimaryToken(refreshedToken);

      // while impersonating, the admin's own session is refreshed in the
      // background but is not the one the socket is carrying.
      if (!isImpersonating()) {
        this.reconnectRealTimeConnection(refreshedToken);
      }
    }

    return response;
  }

  public handleRequestError(
    e: unknown,
    customErrorMessage?: string,
    overrideErrorCodes?: [number],
    userAction?: string,
  ): never {
    let error: unknown;

    if (axios.isAxiosError(e)) {
      const axiosError = e as AxiosError;
      const axiosMessage = axiosError?.response?.status
        ? `${axiosError?.response?.status} error: ${customErrorMessage ?? axiosError.message}`
        : (customErrorMessage ?? axiosError.message);
      error = new AxiosError(
        axiosMessage,
        axiosError.code,
        axiosError.config,
        axiosError.request,
        axiosError.response,
      );
    } else {
      error = customErrorMessage ?? e;
    }

    const errorMessage = (error as Error)?.message || 'Unknown error';
    let errorRequest = 'No request data';
    let errorResponse = 'No response data';
    let statusCode: number | undefined;

    if (axios.isAxiosError(error)) {
      const failedRequestUrl = buildFailedRequestUrl(error.config?.url);

      if (failedRequestUrl) {
        errorRequest = `${getFailedRequestMethod(error)} ${failedRequestUrl}`;
      }

      if (error.response) {
        errorResponse =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
        statusCode = Number(error.response.status);
      }
    }

    const overrideError = shouldOverrideError(statusCode, overrideErrorCodes);
    // Status 0 means the request never reached the backend (network drop, tab close, VPN).
    // These are not actionable — suppress from Sentry like ERR_CANCELED.
    const isClientNetworkError =
      axios.isAxiosError(error) && !statusCode && !(error as AxiosError).response;

    const isCapturedByResponseInterceptor =
      typeof e === 'object' && e !== null && errorsCapturedInSentry.has(e);

    // We want to continue passing 500 errors to Sentry, even if we don't want to display the error toasts for them
    const shouldCaptureInSentry =
      (!overrideError || String(statusCode)?.startsWith('5')) &&
      !isClientNetworkError &&
      !isCapturedByResponseInterceptor;

    // Deliberately a different event shape from `captureApiErrorInSentry`: what
    // reaches here is a non-axios error or an expected status with no route to
    // group by, so it keeps the operation message as its fingerprint and carries
    // the request/response detail that the per-endpoint capture omits.
    if (shouldCaptureInSentry) {
      Sentry.withScope((scope) => {
        scope.setTag('axios_error', axios.isAxiosError(error));
        scope.setExtra('error_message', errorMessage);
        scope.setExtra('error_request', errorRequest);
        scope.setExtra('error_response', errorResponse);
        if (statusCode !== undefined) {
          scope.setExtra('error_status', statusCode);
        }
        // Set the fingerprint to group by error message
        scope.setFingerprint([errorMessage]);
        Sentry.captureException(error);
      });
    }

    // Handling the error response here allows us to display the custom error message in the error toast.
    // `handleErrorResponse` returns a rejected promise as a side-effect artifact; the real error is
    // re-thrown synchronously below. We swallow the rejection so it does not surface as an unhandled
    // rejection captured by Sentry's global handler (TRE-15068).
    void this.handleErrorResponse(
      error as AxiosError,
      !!customErrorMessage,
      overrideErrorCodes,
      userAction,
    ).catch(() => {
      // no-op: rejection is expected; the real error is re-thrown below
    });

    throw error;
  }

  private handleErrorResponse(
    error: AxiosError,
    includeTreadAlertedText: boolean,
    overrideErrorCodes?: number[],
    userAction?: string,
  ): Promise<never> {
    if (error) {
      const requestWasCancelled = axios.isCancel(error) || error.code === 'ERR_CANCELED';
      if (requestWasCancelled) {
        return Promise.reject(error);
      }

      const status = Number(get(error, 'response.status', 0));
      const message =
        error.message ||
        get(error, 'response.error.message') ||
        get(error, 'response.data.message') ||
        STATUS_MESSAGES[500];
      const overrideError = shouldOverrideError(status, overrideErrorCodes);
      if (overrideError) {
        return Promise.reject(error);
      }

      const errorType = String(get(error, 'response.data.error.error_type', ''));

      if ([400, 422, 404].includes(status)) {
        const errorList = get(error, 'response.data.error.errors', [])
          .map((err: { message: string }) => err.message)
          .join(', ');

        emitError(
          errorList || message || STATUS_MESSAGES[status as keyof typeof STATUS_MESSAGES],
          includeTreadAlertedText,
          userAction,
        );
      } else if (status === 401) {
        // Auth required
        // RedirectToLogin
        switch (errorType) {
          case 'member_reset_password':
            emitError(ERROR_TYPES.member_reset_password);
            break;
          case 'breached_password':
            emitError(ERROR_TYPES.breached_password);
            break;
          case 'unable_to_auth_magic_link':
            emitError(ERROR_TYPES.unable_to_auth_magic_link);
            break;
          case 'member_password_not_found':
            emitError(ERROR_TYPES.member_password_not_found);
            break;
          case 'weak_password':
            emitError(ERROR_TYPES.weak_password);
            break;
          default:
            // the impersonation token expires on its own (~1hr) while the
            // admin's own session is still good, so an expiry drops back to
            // that session instead of signing them out. no toast: the reload
            // that follows would discard it anyway.
            if (!isImpersonating()) {
              emitError(STATUS_MESSAGES[401]);
            }

            endUnauthenticatedSession();
            break;
        }
      } else if (status === 403) {
        // Unauthorized
        emitError(message || STATUS_MESSAGES[403], includeTreadAlertedText);
      } else if (isServerError(status)) {
        // Any 500 error
        emitError(message || STATUS_MESSAGES[500], includeTreadAlertedText);
      } else if (!status) {
        emitError(message);
      } else if (message || STATUS_MESSAGES[409]) {
        emitError(message || STATUS_MESSAGES[409], includeTreadAlertedText);
      }
      console.error(message || status || 'Unknown error');
      return Promise.reject(error);
    }
    return Promise.reject('Unknown error');
  }
}

/**
 * Determines whether the given error code should be overridden based on the provided error codes.
 *
 * @param {number | undefined} errorCode - The error code to check.
 * @param {number[] | undefined} overrideErrorCodes - The list of error codes to compare against.
 * @return {boolean} Returns true if the error code should be overridden, false otherwise.
 */
const shouldOverrideError = (
  errorCode: number | undefined,
  overrideErrorCodes: number[] | undefined,
) => {
  if (!overrideErrorCodes || !errorCode) {
    return false;
  }

  const staticSet = new Set([400, 422, 404, 401, 403]);
  if (overrideErrorCodes.some((code) => staticSet.has(code))) {
    return overrideErrorCodes.some((code) => code === errorCode);
  }

  // check if the error code starts with 5
  if (overrideErrorCodes.some((code) => code.toString().startsWith('5'))) {
    return errorCode.toString().startsWith('5');
  }

  return false;
};

const connection = new Connection();
export default connection;
